import { useEffect, useRef } from "react";
import { useAuth } from "../auth/AuthProvider";
import { identify } from "../lib/gtm";

/*
 * Who the events belong to.
 *
 * A component rather than a hook call inside an existing one, because it has to
 * sit under `AuthProvider` and above the router without being a route — the
 * signed-out pages are siblings of the authenticated tree, and identity has to
 * be pushed for a session that begins on `/login` as much as one that resumes
 * on `/apps`.
 *
 * Renders nothing. Pageviews are handled outside React entirely, by subscribing
 * to the router in `main.tsx`: a navigation is a router event, and routing it
 * through a component would have meant a pathless layout route and reindenting
 * every route in the file to gain nothing.
 */
export function AnalyticsIdentity() {
  const { user, currentOrg } = useAuth();

  /*
   * Pushed only when it actually changes, which is why the previous value is
   * held rather than left to the effect's dependencies. `reloadSession`
   * replaces the user object with an equal one, and each replacement would
   * otherwise be another identity event describing a change that did not
   * happen.
   */
  const last = useRef<string | null>(null);

  useEffect(() => {
    const next = `${user?.id ?? ""}:${currentOrg?.id ?? ""}`;
    if (last.current === next) return;
    last.current = next;
    identify(user?.id ?? null, currentOrg?.id ?? null);
  }, [user?.id, currentOrg?.id]);

  return null;
}
